import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Factory, MapPin, Users, Percent, Truck } from "lucide-react";
import { Panel, KV, Chip, BackLink, RawJson, money, dateGB, asText } from "./_shared";

import DataAudit from "./_dataAudit";
import LinkedRecords from "./LinkedRecords";
import { RecordHistory } from "./v2/_shared";
const pct = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return `${(n * 100).toFixed(2)}%`;
};

function ContactCard({ n, name, email, tel }) {
  if (!name && !email && !tel) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-1">Contact {n}</div>
      <div className="text-sm font-semibold text-white truncate">{asText(name)}</div>
      {email && (
        <a href={`mailto:${email}`} className="block font-mono text-xs text-[#C8A46B] hover:text-[#DAB58A] break-all mt-0.5">
          {email}
        </a>
      )}
      {tel && (
        <a href={`tel:${String(tel).replace(/\s+/g, "")}`} className="block font-mono text-xs text-slate-300 hover:text-white mt-0.5">
          {tel}
        </a>
      )}
    </div>
  );
}

export default function LabSupplierDetail() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/lab/suppliers/${id}`);
        if (alive) setDoc(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Failed to load supplier");
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [id]);

  if (loading) return <Layout><div className="text-center text-slate-500 py-20">Loading supplier…</div></Layout>;
  if (err || !doc) return <Layout><div className="max-w-xl mx-auto text-center py-20 text-red-400">{err || "Supplier not found"}</div></Layout>;

  const addressLines = [doc.AddressLine1, doc.AddressLine2, doc.AddressLine3, doc.City, doc.County, doc.Postcode, doc.Country]
    .filter(Boolean);

  const shipping = Array.isArray(doc.ShippingCosts) ? doc.ShippingCosts : [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="lab-supplier-detail-page">
        <BackLink to="/lab/suppliers" label="Data Lab · Suppliers" />

        {/* Identity strip */}
        <div className="rounded-xl border border-white/10 bg-gradient-to-br from-[#0F1420] via-[#0F1420] to-[#1a1223] p-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-2">
                {doc.Enabled === true && <Chip tone="green">Live</Chip>}
                {doc.Enabled === false && <Chip tone="red">Disabled</Chip>}
                {doc.AccountNumber && <Chip tone="orange">Acct {doc.AccountNumber}</Chip>}
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white break-words">{asText(doc.SupplierName)}</h1>
              {addressLines.length > 0 && (
                <div className="text-sm text-slate-300 mt-1 flex items-start gap-1.5">
                  <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-slate-500" />
                  <span>{addressLines.join(", ")}</span>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1 mt-3 text-xs text-slate-400 font-mono">
                <span>Doc {asText(doc._id)}</span>
                {doc._received_at && <span>Synced {dateGB(doc._received_at)}</span>}
                {doc.WebPage && (
                  <a href={/^https?:/.test(doc.WebPage) ? doc.WebPage : `https://${doc.WebPage}`}
                     target="_blank" rel="noreferrer"
                     className="text-[#C8A46B] hover:text-[#DAB58A] underline decoration-[#C8A46B]/30">
                    {doc.WebPage}
                  </a>
                )}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-widest text-slate-500 font-mono">Commercial</div>
              <div className="mt-1 flex flex-col items-end gap-1">
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mr-2">Rebate</span>
                  <span className="font-mono text-2xl font-bold text-teal-300">{pct(doc.PercentRebate)}</span>
                </div>
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mr-2">Discount</span>
                  <span className="font-mono text-2xl font-bold text-amber-300">{pct(doc.PercentDiscount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Contacts */}
            <Panel icon={Users} title="Contacts" testid="panel-contacts"
              right={<span>{[doc.ContactName1, doc.ContactName2, doc.ContactName3].filter(Boolean).length} people</span>}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <ContactCard n={1} name={doc.ContactName1} email={doc.ContactName1Email} tel={doc.ContactName1TelNo} />
                <ContactCard n={2} name={doc.ContactName2} email={doc.ContactName2Email} tel={doc.ContactName2TelNo} />
                <ContactCard n={3} name={doc.ContactName3} email={doc.ContactName3Email} tel={doc.ContactName3TelNo} />
              </div>
            </Panel>

            {/* Address */}
            <Panel icon={MapPin} title="Address" testid="panel-address">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <KV label="Address line 1" value={asText(doc.AddressLine1)} wide />
                <KV label="Address line 2" value={asText(doc.AddressLine2)} />
                <KV label="Address line 3" value={asText(doc.AddressLine3)} />
                <KV label="City"           value={asText(doc.City)} />
                <KV label="County"         value={asText(doc.County)} />
                <KV label="Postcode"       value={<span className="font-mono text-slate-100">{asText(doc.Postcode)}</span>} />
                <KV label="Country"        value={asText(doc.Country)} />
              </div>
            </Panel>

            {/* Shipping bands */}
            <Panel icon={Truck} title="Shipping cost bands" testid="panel-shipping"
              right={<span>{shipping.length} band{shipping.length === 1 ? "" : "s"}</span>}>
              {shipping.length === 0 ? (
                <div className="text-sm text-slate-500">No shipping bands configured.</div>
              ) : (
                <div className="overflow-x-auto rounded-lg border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="bg-white/[0.04] text-[10px] uppercase tracking-widest text-slate-400 font-mono">
                      <tr>
                        <th className="px-3 py-2 text-left">Min order (ex VAT)</th>
                        <th className="px-3 py-2 text-left">Postage (ex VAT)</th>
                        <th className="px-3 py-2 text-left">Note</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipping.map((b, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="px-3 py-2 font-mono text-white">{money(b?.MinOrderValueExcVAT)}</td>
                          <td className="px-3 py-2 font-mono">
                            {Number(b?.PostageExcVAT) === 0
                              ? <Chip tone="green">FREE</Chip>
                              : <span className="text-[#C8A46B]">{money(b?.PostageExcVAT)}</span>}
                          </td>
                          <td className="px-3 py-2 text-slate-300 text-xs">{asText(b?.Note)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <RawJson data={doc} title="Raw document" testid="panel-raw" />
          </div>

          <div className="space-y-6">
            {/* Commercial terms */}
            <Panel icon={Percent} title="Commercial terms" testid="panel-commercial">
              <div className="space-y-3">
                <KV label="Account number" value={<span className="font-mono text-[#C8A46B]">{asText(doc.AccountNumber)}</span>} />
                <KV label="Supplier name"  value={asText(doc.SupplierName)} />
                <KV label="Rebate"         value={<span className="font-mono text-teal-300">{pct(doc.PercentRebate)}</span>} />
                <KV label="Discount"       value={<span className="font-mono text-amber-300">{pct(doc.PercentDiscount)}</span>} />
                <KV label="Enabled"        value={
                  doc.Enabled === true ? <Chip tone="green">Live</Chip>
                  : doc.Enabled === false ? <Chip tone="red">Disabled</Chip>
                  : "—"} />
                <KV label="Fax"            value={<span className="font-mono">{asText(doc.FaxNumber)}</span>} />
                <KV label="Web"            value={asText(doc.WebPage)} />
              </div>
            </Panel>
          </div>
        </div>
      </div>
    
      <LinkedRecords collection="suppliers" id={id} />
      <DataAudit doc={doc} title="Data audit — full record" testid="lab-data-audit" />
      <RecordHistory collection="suppliers" docId={id} testid="lab-supplier-history" />
    </Layout>
  );
}
